import { Matrix4, type Object3D } from 'three';
import type { TransformControls } from 'three/addons/controls/TransformControls.js';
import { hotkeyRegistry } from '../debug/hotkeyRegistry';
import { isTextEntryTarget } from '../debug/keyboardTarget';
import { devBus } from '../debug/devBus';

// Bounded undo/redo stack for gizmo edits.
//
// Capture model: on 'mouseDown' (drag start) we snapshot the target's local
// matrix as the "before" state. On 'mouseUp' (drag end) we snapshot the
// post-state and push a record to the undo stack. ⌘Z / ⌃Z pops the latest
// record and restores the "before"; ⌘⇧Z / ⌃⇧Z (or ⌘Y) restores the "after".
//
// Why local matrices: gizmo edits in this app are always on the object's
// own transform (translate/rotate), never on its parent. Snapshotting
// .matrix is cheaper than re-decomposing position/quaternion/scale and is
// lossless for the data we care about.
//
// The two TransformControls instances (translate + rotate) share one stack:
// pointer can only drag one at a time, and undoing a translate after a
// rotate is the natural expectation.
export interface GizmoUndoDeps {
  controls: TransformControls[];
  /** Cap on stack size — old entries drop off the bottom. */
  maxDepth?: number;
  /** A competing undo surface (the timeline's). On ⌘Z the arbiter undoes
   *  whichever of the two edited most recently — so a timeline edit after a
   *  gizmo drag undoes the timeline edit, leaving the 3D transform untouched
   *  (U1). DM8 — the arbiter now also walks REDO across both surfaces
   *  (lastUndoneAt/redo) and FALLS BACK to the other stack when the recency-
   *  chosen one turns out empty, so ⌘Z/⌘⇧Z never silently no-op while any
   *  surface still has something to undo/redo. */
  peerUndo?: {
    lastEditAt: () => number;
    undo: () => boolean;
    lastUndoneAt?: () => number;
    redo?: () => boolean;
  };
  /** DM6 — the persistence aspect a drag-end commit belongs to ('manifest' for a
   *  hero, 'atmosphere' for a shaft handle). Read at drag-end so the auto-save
   *  hub knows which file to flush. */
  currentAspect?: () => string | undefined;
  /** DM6 — fired after any scene-mutating stack op (drag-end push, command push,
   *  undo, redo) with the record's aspect. The app marks that aspect dirty so the
   *  edit — INCLUDING an undo, which re-commits the reverted state — reaches disk
   *  through the save-on-commit flow. This is what dissolves DM1's clear-on-save:
   *  the undo stack no longer needs severing because disk always follows the scene. */
  onCommit?: (aspect?: string) => void;
}

interface UndoRecord {
  /** performance.now() at push — lets the ⌘Z arbiter compare recency against
   *  a peer undo stack (the timeline's), so last-edit-wins (U1). */
  at: number;
  // Transform record (gizmo drag / numeric scale): restore the local matrix.
  obj?: Object3D;
  before?: Matrix4;
  after?: Matrix4;
  // Command record (DM4 — retag, and any non-transform edit): explicit
  // undo/redo closures. Both kinds share the ONE stack so ⌘Z walks every edit
  // in recency order (toward DM8's one-undo model).
  cmd?: { undo: () => void; redo: () => void };
  // DM6 — the persistence aspect this edit belongs to ('manifest'/'atmosphere'),
  // so an undo/redo can re-commit the right file through the save-on-commit hub.
  aspect?: string;
}

export interface GizmoUndo {
  /** Push a transform record manually (e.g. after a programmatic move).
   *  `aspect` tags it for save-on-commit re-persistence (DM6). */
  push(obj: Object3D, before: Matrix4, after: Matrix4, aspect?: string): void;
  /** Push a non-transform edit as explicit undo/redo closures (DM4 — e.g. a
   *  hero retag). Shares the one stack + recency arbitration. */
  pushCommand(undo: () => void, redo: () => void, aspect?: string): void;
  undo(): boolean;
  redo(): boolean;
  /** Wipe both stacks — call after a save so undo can't roll back persisted edits. */
  clear(): void;
  dispose(): void;
}

const restoreLocal = (obj: Object3D, m: Matrix4): void => {
  // Decompose + reassign so children rendered relative to this object stay
  // visually consistent. obj.matrix.copy(m) alone wouldn't update
  // position/quaternion/scale, and the next .updateMatrix() would clobber it.
  m.decompose(obj.position, obj.quaternion, obj.scale);
  obj.updateMatrix();
};

export function createGizmoUndo(deps: GizmoUndoDeps): GizmoUndo {
  const maxDepth = deps.maxDepth ?? 100;
  const undoStack: UndoRecord[] = [];
  const redoStack: UndoRecord[] = [];

  // Per-controller capture of "before" matrix while a drag is in flight.
  // A controller without an active drag has no entry.
  const draggingFrom = new Map<TransformControls, { obj: Object3D; before: Matrix4 }>();

  const push = (obj: Object3D, before: Matrix4, after: Matrix4, aspect?: string): void => {
    undoStack.push({ obj, before, after, at: performance.now(), aspect });
    if (undoStack.length > maxDepth) undoStack.shift();
    // Any new edit invalidates the redo branch — same model as every text
    // editor's undo, avoids weird "history forks" UX.
    redoStack.length = 0;
    deps.onCommit?.(aspect); // DM6 — persist the edit through save-on-commit
    devBus.emit('gizmo:edit', { action: 'commit' }); // DM14 — narrate the edit
  };

  const pushCommand = (undoFn: () => void, redoFn: () => void, aspect?: string): void => {
    undoStack.push({ at: performance.now(), cmd: { undo: undoFn, redo: redoFn }, aspect });
    if (undoStack.length > maxDepth) undoStack.shift();
    redoStack.length = 0;
    deps.onCommit?.(aspect);
    devBus.emit('gizmo:edit', { action: 'commit' }); // DM14
  };

  const onMouseDown = (controls: TransformControls): void => {
    const obj = controls.object;
    if (!obj) return;
    draggingFrom.set(controls, { obj, before: obj.matrix.clone() });
  };

  const onMouseUp = (controls: TransformControls): void => {
    const start = draggingFrom.get(controls);
    draggingFrom.delete(controls);
    if (!start) return;
    const after = start.obj.matrix.clone();
    // No-op drags (click without move) shouldn't pollute the stack.
    if (matricesEqual(start.before, after)) return;
    push(start.obj, start.before, after, deps.currentAspect?.());
  };

  const listenersPerControls: Array<{ controls: TransformControls; down: () => void; up: () => void }> = [];
  for (const c of deps.controls) {
    const down = () => onMouseDown(c);
    const up = () => onMouseUp(c);
    c.addEventListener('mouseDown', down);
    c.addEventListener('mouseUp', up);
    listenersPerControls.push({ controls: c, down, up });
  }

  const undo = (): boolean => {
    const rec = undoStack.pop();
    if (!rec) return false;
    if (rec.cmd) rec.cmd.undo();
    else restoreLocal(rec.obj!, rec.before!);
    redoStack.push(rec);
    deps.onCommit?.(rec.aspect); // DM6 — undo re-commits the reverted state to disk
    devBus.emit('gizmo:edit', { action: 'undo' }); // DM14
    return true;
  };

  const redo = (): boolean => {
    const rec = redoStack.pop();
    if (!rec) return false;
    if (rec.cmd) rec.cmd.redo();
    else restoreLocal(rec.obj!, rec.after!);
    undoStack.push(rec);
    deps.onCommit?.(rec.aspect);
    devBus.emit('gizmo:edit', { action: 'redo' }); // DM14
    return true;
  };

  const myUndoAt = (): number => (undoStack.length ? undoStack[undoStack.length - 1].at : 0);
  const myRedoAt = (): number => (redoStack.length ? redoStack[redoStack.length - 1].at : 0);

  // DM8 — one undo/redo model across surfaces. Pick whichever of gizmo / timeline
  // edited (or was-undone) most recently; if that stack turns out empty, FALL
  // BACK to the other. So ⌘Z always undoes the genuine last action wherever it
  // happened — or clearly does nothing only when BOTH stacks are empty.
  const arbitratedUndo = (): boolean => {
    const peerAt = deps.peerUndo?.lastEditAt() ?? 0;
    if (peerAt > myUndoAt()) return (deps.peerUndo!.undo() || undo());
    return undo() || (deps.peerUndo?.undo() ?? false);
  };
  const arbitratedRedo = (): boolean => {
    const peerAt = deps.peerUndo?.lastUndoneAt?.() ?? 0;
    if (peerAt > myRedoAt()) return ((deps.peerUndo?.redo?.() ?? false) || redo());
    return redo() || (deps.peerUndo?.redo?.() ?? false);
  };

  const onKey = (e: KeyboardEvent): void => {
    // Only fire when the user is genuinely in the 3D viewport — don't steal
    // keyboard from a focused text input / dropdown (U2).
    if (isTextEntryTarget(e)) return;
    if (!(e.metaKey || e.ctrlKey)) return;
    if (e.key === 'z' || e.key === 'Z') {
      e.preventDefault();
      if (e.shiftKey) arbitratedRedo(); else arbitratedUndo();
    } else if (e.key === 'y' || e.key === 'Y') {
      // Windows-style redo.
      e.preventDefault();
      arbitratedRedo();
    }
  };
  window.addEventListener('keydown', onKey);
  const unregHotkeys = [
    hotkeyRegistry.register({ keys: '⌘Z / Ctrl-Z', description: 'undo gizmo edit', section: 'editing', order: 2 }),
    hotkeyRegistry.register({ keys: '⌘⇧Z / ⌘Y', description: 'redo gizmo edit', section: 'editing', order: 3 }),
  ];

  const clear = (): void => {
    undoStack.length = 0;
    redoStack.length = 0;
  };

  const dispose = (): void => {
    for (const { controls, down, up } of listenersPerControls) {
      controls.removeEventListener('mouseDown', down);
      controls.removeEventListener('mouseUp', up);
    }
    window.removeEventListener('keydown', onKey);
    for (const u of unregHotkeys) u();
    clear();
  };

  return { push, pushCommand, undo, redo, clear, dispose };
}

// Cheap "are these matrices the same to 5 decimal places" — used to skip
// no-op drags. epsilon chosen well above float32 noise but small enough
// that any real translate (≥1mm) registers.
function matricesEqual(a: Matrix4, b: Matrix4, eps = 1e-5): boolean {
  const ea = a.elements;
  const eb = b.elements;
  for (let i = 0; i < 16; i++) {
    if (Math.abs(ea[i] - eb[i]) > eps) return false;
  }
  return true;
}
